/**
 * Converts a snake-case string to camelCase
 *
 * @param {string} str kebab-cased string
 * @returns {string} camelCased string
 */
export function kebabToCamelCase(str) {
  return str.replace(/(-)([a-z])/g, (g) => g[1].toUpperCase());
}

/**
 * Converts a camelCase string to kebab-case
 *
 * @param {string} str camelCased string
 * @returns {string} kebab-cased string
 */
export function camelToKebabCase(str) {
  return str.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
}

/**
 * Takes attributes from element and creates an object
 * with the keys camelCased.
 *
 * @param {NamedNodeMap} attributes Element.attributes
 * @returns {Record<string, string>} Object with camelCased keys
 */
export function attributesToObject(attributes) {
  return attributes
    ? Array.from(attributes).reduce(
        (acc, { localName, value }) =>
          Object.assign(acc, {
            [kebabToCamelCase(localName)]: value,
          }),
        {},
      )
    : {};
}

/**
 * Converts a string of HTML into nodes.
 *
 * @param {string} string HTML in string form
 * @returns {DocumentFragment} Nodes parsed from the HTML string
 */
export function stringToElements(string) {
  return document.createRange().createContextualFragment(string);
}

/**
 * @param {TemplateStringsArray} strings
 * @param {...any} rest
 * @returns {CSSStyleSheet}
 */
export function modernCSS(strings, ...rest) {
  const sheet = new CSSStyleSheet();
  sheet.replaceSync(String.raw(strings, ...rest));
  return sheet;
}

/**
 * @param {TemplateStringsArray} strings
 * @param {...any} rest
 * @returns {HTMLStyleElement}
 */
export function oldCSS(strings, ...rest) {
  const style = document.createElement("style");
  style.innerText = String.raw(strings, ...rest);
  return style;
}

const supportsConstructableStylesheets = (() => {
  try {
    // eslint-disable-next-line no-new
    new CSSStyleSheet();
    return true;
  } catch (_e) {
    return false;
  }
})();

/**
 * @param {TemplateStringsArray} strings
 * @param {any[]} rest
 * @returns {CSSStyleSheet | HTMLStyleElement}
 */
export function css(strings, ...rest) {
  return supportsConstructableStylesheets
    ? modernCSS(strings, ...rest)
    : oldCSS(strings, ...rest);
}

/**
 * @export
 * @param {TemplateStringsArray} strings
 * @param {any[]} rest
 * @returns {DocumentFragment}
 */
export function html(strings, ...rest) {
  return stringToElements(String.raw(strings, ...rest));
}

export class EventHub {
  /** @param {ShadowRoot|HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.byType = new Map(); // type -> array of {selector, fn, options}
  }

  /**
   * @param {string} type
   * @param {string} selector
   * @param {EventListener} fn
   * @param {EventListenerOptions} [options]
   */
  on(type, selector, fn, options) {
    if (!this.byType.has(type)) {
      this.byType.set(type, []);
      // Attach ONCE per type. The listener is "this" (handleEvent).
      this.root.addEventListener(type, this, options);
    }
    this.byType.get(type).push({ selector, fn });
  }

  offAll() {
    for (const type of this.byType.keys()) {
      this.root.removeEventListener(type, this);
    }
    this.byType.clear();
  }

  /** @param {Event} event */
  handleEvent(event) {
    const list = this.byType.get(event.type);
    if (!list) return;

    // Event delegation: walk from target upwards.
    for (const { selector, fn } of list) {
      const matched = event.target instanceof Element ? event.target.closest(selector) : null;
      if (
        matched &&
        (this.root === matched.getRootNode?.() || this.root.contains(matched))
      ) {
        fn.call(matched, event); // context = matched element (often what you want)
      }
    }
  }
}
